'use client'

import Link from 'next/link'
import {
  formatEditorDisplayName,
  formatEditorHandleDistinct,
  getAuthorAvatarUrl,
  getAuthorInitial,
  getAuthorProfileHref,
} from '@/lib/author-url'
import type { ShortFeedItem } from '@/lib/news-types'
import { getImageUrl } from '@/lib/utils'
import { IMAGE_PRESETS } from '@/lib/image-presets'
import styles from '@/components/shorts/shorts.module.css'

interface ShortsAuthorRowProps {
  editor: NonNullable<ShortFeedItem['editor']>
  youtubeChannel?: ShortFeedItem['youtubeChannel']
  onOpenDescription?: () => void
}

function resolveAvatarSrc(
  editor: NonNullable<ShortFeedItem['editor']>,
  youtubeChannel?: ShortFeedItem['youtubeChannel'],
): string | null {
  const fromEditor = getAuthorAvatarUrl(editor)
  const fromChannel = youtubeChannel?.avatarUrl?.trim() || null
  const raw = fromEditor || fromChannel
  if (!raw) return null
  if (/^https?:\/\//i.test(raw)) return raw
  return getImageUrl(raw, IMAGE_PRESETS.avatar)
}

export default function ShortsAuthorRow({
  editor,
  youtubeChannel,
  onOpenDescription,
}: ShortsAuthorRowProps) {
  const profileHref = getAuthorProfileHref(editor)
  const channelUrl = youtubeChannel?.url?.trim() || null
  const externalHref = !profileHref && editor.id === 0 ? channelUrl : null
  const displayName =
    formatEditorDisplayName(editor) || youtubeChannel?.name?.trim() || null
  const handle = formatEditorHandleDistinct(editor)
  const avatarSrc = resolveAvatarSrc(editor, youtubeChannel)
  const initial = getAuthorInitial(editor)

  const content = (
    <>
      <span className={styles.authorAvatar} aria-hidden>
        {avatarSrc ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={avatarSrc}
            alt=""
            className={styles.authorAvatarImg}
            referrerPolicy="no-referrer"
          />
        ) : (
          initial
        )}
      </span>
      <span className={styles.authorMeta}>
        {displayName ? <span className={styles.authorName}>{displayName}</span> : null}
        {handle ? <span className={styles.authorHandle}>{handle}</span> : null}
      </span>
    </>
  )

  if (profileHref) {
    return (
      <Link
        href={profileHref}
        className={styles.authorRow}
        data-shorts-control
        onPointerDown={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {content}
      </Link>
    )
  }

  if (externalHref) {
    return (
      <a
        href={externalHref}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.authorRow}
        data-shorts-control
        onPointerDown={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {content}
      </a>
    )
  }

  return (
    <button
      type="button"
      className={styles.authorRow}
      data-shorts-control
      onPointerDown={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation()
        onOpenDescription?.()
      }}
    >
      {content}
    </button>
  )
}
